// auth.js
import React, { useState, useEffect, createContext, useContext } from "react";
import tenantResolver from "../services/tenantResolver";
import Logger from "../utils/logger";

const AuthContext = createContext();

// Create an authentication provider component
function AuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(
    localStorage.getItem("accessToken")
  );
  const [userData, setUserData] = useState(() => {
    const storedUserData = localStorage.getItem("userData");
    return storedUserData ? JSON.parse(storedUserData) : null;
  });
  const [tenantInitialized, setTenantInitialized] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);

  // Function to update user data (to be called after login)
  const updateUserData = (newUserData) => {
    console.log('[AuthProvider] updateUserData called with:', {
      hasNewUserData: !!newUserData,
      userDataKeys: newUserData ? Object.keys(newUserData) : [],
      access: newUserData?.access,
      tenantId: newUserData?.tenantId,
      fullData: newUserData
    });
    
    // Validate that tenantId exists in user data
    if (!newUserData?.tenantId) {
      Logger.warn('[AuthProvider] User data missing tenantId - this may cause API request failures');
    } else {
      console.log('[AuthProvider] Setting tenant from userData:', newUserData.tenantId);
      tenantResolver.setTenantFromUserData(newUserData.tenantId);
    }
    
    setUserData(newUserData);
    setAuthenticated(true);
    console.log('[AuthProvider] State updated - authenticated set to true');
  };

  // Function to logout
  const logout = () => {
    console.log('[AuthProvider] logout called');
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("tenant"); // Also clear tenant info
    setUserData(null);
    setAuthenticated(false);
    console.log('[AuthProvider] Logout completed - localStorage cleared, state reset');
  };

  useEffect(() => {
    // Check if the user is authenticated
    const getUserData = () => {
      const storedUserData = localStorage.getItem("userData");
      return storedUserData ? JSON.parse(storedUserData) : null;
    };

    const isAuthenticated = () => {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        const userData = getUserData();
        if (userData) {
          setUserData(userData);
          // Set tenant from stored userData if available
          if (userData.tenantId) {
            console.log('[AuthProvider] Setting tenant from stored userData:', userData.tenantId);
            tenantResolver.setTenantFromUserData(userData.tenantId);
          }
        }
        return true;
      }
      return false;
    };

    // Initialize tenant resolver (for pre-login scenarios)
    const initializeTenant = () => {
      try {
        Logger.info('Initializing tenant resolver...');
        
        // Check userData FIRST before initializing from hostname
        const storedUserData = getUserData();
        if (storedUserData?.tenantId) {
          // User is logged in - set tenant from userData directly
          Logger.info('Setting tenant from stored userData before initialization', { tenantId: storedUserData.tenantId });
          tenantResolver.setTenantFromUserData(storedUserData.tenantId);
          setTenantInfo({ slug: storedUserData.tenantId });
        } else {
          // No userData - initialize from hostname (pre-login scenario)
          const tenantSlug = tenantResolver.initialize();
          setTenantInfo({ slug: tenantSlug });
        }
        
        // Don't fetch organization config - use static branding
        setTenantInitialized(true);
        Logger.info('Tenant initialization completed');
      } catch (error) {
        Logger.error('Failed to initialize tenant:', error);
        setTenantInitialized(true); // Continue with fallback config
      }
    };

    // Check authentication first to get userData, then initialize tenant
    const isUserAuthenticated = isAuthenticated();
    setAuthenticated(isUserAuthenticated);
    // Initialize tenant after checking auth (so userData is available)
    initializeTenant();
  }, []); // Run only once when the component is mounted

  return (
    <AuthContext.Provider value={{ 
      authenticated, 
      userData, 
      setUserData: updateUserData,
      logout,
      tenantInitialized,
      tenantInfo 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
